import React from "react";

import {
  NavLink,
  useNavigate
} from "react-router-dom";

import {
  LayoutDashboard,
  FolderTree,
  Package,
  Tag,
  Users,
  MessageSquare,
  CreditCard,
  X,
  LogOut,
  Plus,
  List
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";

import {
  canAccessUsers,
  canAccessCategories,
  canAccessOffers,
  canAccessPayments
} from "../../utils/permissions";


const Sidebar = ({
  mobileOpen,
  onClose
}) => {

  const navigate = useNavigate();


  /*
  |--------------------------------------------------------------------------
  | AUTH
  |--------------------------------------------------------------------------
  */

  const {
    user,
    logout
  } = useAuth();


  /*
  |--------------------------------------------------------------------------
  | NAVIGATION
  |--------------------------------------------------------------------------
  */

  const navigation = [

    /*
    |--------------------------------------------------------------------------
    | DASHBOARD
    |--------------------------------------------------------------------------
    */

    {
      label: "Dashboard",
      path: "/dashboard",
      icon: LayoutDashboard,
      show: true
    },


    /*
    |--------------------------------------------------------------------------
    | CATEGORIES
    |--------------------------------------------------------------------------
    */

    {
      label: "Categories",
      path: "/categories",
      icon: FolderTree,
      show:
        canAccessCategories(user)
    },


    /*
    |--------------------------------------------------------------------------
    | OFFERINGS
    |--------------------------------------------------------------------------
    |
    | Offerings are the products/services displayed
    | on the public website.
    |
    */

    {
      label: "Offerings",
      path: "/offerings",
      icon: Package,
      show: true
    },


    /*
    |--------------------------------------------------------------------------
    | OFFERS
    |--------------------------------------------------------------------------
    */

    {
      label: "Offers",
      path: "/offers",
      icon: Tag,
      show:
        canAccessOffers(user)
    },


    /*
    |--------------------------------------------------------------------------
    | PAYMENTS
    |--------------------------------------------------------------------------
    */

    {
      label: "Payments",
      path: "/payments",
      icon: CreditCard,
      show:
        canAccessPayments(user)
    },


    /*
    |--------------------------------------------------------------------------
    | QUERIES
    |--------------------------------------------------------------------------
    |
    | Authenticated users can view and manage website queries.
    |
    */

    {
      label: "Queries",
      path: "/queries",
      icon: MessageSquare,
      show: true
    },


    /*
    |--------------------------------------------------------------------------
    | USERS
    |--------------------------------------------------------------------------
    */

    {
      label: "Users",
      path: "/users",
      icon: Users,
      show:
        canAccessUsers(user)
    }

  ];


  /*
  |--------------------------------------------------------------------------
  | LOGOUT
  |--------------------------------------------------------------------------
  */

  const handleLogout = () => {

    logout();

    onClose?.();

    navigate(
      "/login",
      {
        replace: true
      }
    );

  };


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <>

      {/* MOBILE OVERLAY */}

      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}


      {/* SIDEBAR */}

      <aside
        className={`sidebar ${
          mobileOpen
            ? "sidebar-open"
            : ""
        }`}
      >

        {/* LOGO */}

        <div className="sidebar-logo">

          <div className="sidebar-logo-mark">
            H
          </div>

          <div className="sidebar-logo-text">

            <h1>
              HARshait
            </h1>

            <p>
              Management System
            </p>

          </div>


          {/* MOBILE CLOSE */}

          <button
            type="button"
            className="sidebar-close-button"
            onClick={onClose}
            aria-label="Close menu"
          >

            <X size={20} />

          </button>

        </div>


        {/* NAVIGATION */}

        <nav className="sidebar-navigation">

          <p className="sidebar-section-title">
            Main
          </p>


          <div className="sidebar-nav-list">

            {navigation
              .filter(
                (item) =>
                  item.show
              )
              .map((item) => {

                const Icon =
                  item.icon;

                return (

                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `sidebar-nav-link ${
                        isActive
                          ? "sidebar-nav-link-active"
                          : ""
                      }`
                    }
                  >

                    <Icon
                      size={19}
                    />

                    <span>
                      {item.label}
                    </span>

                  </NavLink>

                );

              })}

          </div>


          {/* OFFERING QUICK ACTIONS */}

          <div className="sidebar-sub-navigation">

            <p className="sidebar-section-title">
              Offerings
            </p>


            {/* ALL OFFERINGS */}

            <NavLink
              to="/offerings"
              onClick={onClose}
              className={({ isActive }) =>
                `sidebar-sub-nav-link ${
                  isActive
                    ? "sidebar-sub-nav-link-active"
                    : ""
                }`
              }
            >

              <List size={17} />

              <span>
                All Offerings
              </span>

            </NavLink>


            {/* CREATE OFFERING */}

            <NavLink
              to="/offerings/create"
              onClick={onClose}
              className={({ isActive }) =>
                `sidebar-sub-nav-link ${
                  isActive
                    ? "sidebar-sub-nav-link-active"
                    : ""
                }`
              }
            >

              <Plus size={17} />

              <span>
                Create Offering
              </span>

            </NavLink>

          </div>


          {/* OFFER QUICK ACTIONS */}

          {canAccessOffers(user) && (

            <div className="sidebar-sub-navigation">

              <p className="sidebar-section-title">
                Offers
              </p>


              {/* ALL OFFERS */}

              <NavLink
                to="/offers"
                onClick={onClose}
                className={({ isActive }) =>
                  `sidebar-sub-nav-link ${
                    isActive
                      ? "sidebar-sub-nav-link-active"
                      : ""
                  }`
                }
              >

                <List size={17} />

                <span>
                  All Offers
                </span>

              </NavLink>


              {/* CREATE OFFER */}

              <NavLink
                to="/offers/create"
                onClick={onClose}
                className={({ isActive }) =>
                  `sidebar-sub-nav-link ${
                    isActive
                      ? "sidebar-sub-nav-link-active"
                      : ""
                  }`
                }
              >

                <Plus size={17} />

                <span>
                  Create Offer
                </span>

              </NavLink>

            </div>

          )}

        </nav>


        {/* USER + LOGOUT */}

        <div className="sidebar-user-section">


          {/* USER CARD */}

          <div className="sidebar-user-card">

            <div className="sidebar-user-avatar">

              {user?.name
                ?.charAt(0)
                ?.toUpperCase() ||
                "U"}

            </div>


            <div className="sidebar-user-details">

              <p className="sidebar-user-name">

                {user?.name ||
                  "User"}

              </p>


              <p className="sidebar-user-role">

                {user?.roles?.join(
                  ", "
                ) || "USER"}

              </p>

            </div>

          </div>


          {/* LOGOUT */}

          <button
            type="button"
            className="sidebar-logout-button"
            onClick={handleLogout}
          >

            <LogOut
              size={18}
            />

            <span>
              Logout
            </span>

          </button>

        </div>

      </aside>

    </>
  );
};


export default Sidebar;
